/**
 * "Sign out keeps your notes": the marker left behind when an account logs out
 * WITHOUT wiping the device.
 *
 * The rows stay exactly as they are, still carrying the old account's note
 * ids. That is deliberate and load-bearing in both directions:
 *
 * - Signing back into the SAME account converges for free: the ids match the
 *   server's, so a pull reconciles them as ordinary updates.
 * - Signing into a DIFFERENT account must NOT adopt them in place: the
 *   attachment URLs in their markdown are auth-gated and owned by the old
 *   account, and `[[id|Title]]` links point at its note ids. Nothing copies
 *   between two server accounts, so here the local copy is removed instead.
 *   Nothing is lost either: the old account can always be signed back into.
 *   (Notes written with NO account are the other case entirely — they are
 *   ownerless, never belonged to a server account, and the `local` gate
 *   variant adopts them into whichever account signs in.)
 *
 * ponytail: the cross-account wipe is TOTAL, so notes written in local mode
 * AFTER the keep-logout go with it. Web cannot tell the two apart — every
 * locally-authored row carries `user_id = ''` whether it was written signed in
 * or not (`useNotes` always passes `''`, and `putNote` preserves it), so there
 * is no column to filter on. The gate copy therefore states plainly that a
 * different account clears everything on the device, including anything
 * written since. To make the wipe partial instead, stamp the logout time onto
 * the marker and delete only `created_at <= parkedAt` — plus that note's tags,
 * links and queued attachments, which is why it is not done here.
 *
 * Mirrored in Swift by `AppPreferencesStore.previousOwner*` — change both or
 * neither.
 */

const PREVIOUS_OWNER_KEY = 'caliu-previous-owner'

export interface PreviousOwner {
  userId: string
  /** Shown in the gate ("your notes from name@example.com"); null for a linked Apple account with a withheld email. */
  email: string | null
}

export function rememberPreviousOwner(owner: PreviousOwner): void {
  try {
    localStorage.setItem(PREVIOUS_OWNER_KEY, JSON.stringify(owner))
  } catch {
    // Storage blocked: the notes still stay on the device, but the next
    // sign-in can no longer prove whose they are and takes the safe path
    // (the gate asks, and a mismatch wipes). Expected, not reported.
  }
}

export function getPreviousOwner(): PreviousOwner | null {
  try {
    const raw = localStorage.getItem(PREVIOUS_OWNER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PreviousOwner
    if (!parsed || typeof parsed.userId !== 'string' || !parsed.userId) return null
    return { userId: parsed.userId, email: typeof parsed.email === 'string' ? parsed.email : null }
  } catch {
    return null
  }
}

export function clearPreviousOwner(): void {
  try {
    localStorage.removeItem(PREVIOUS_OWNER_KEY)
  } catch {
    // Nothing else to do.
  }
}

/**
 * What a completed sign-in must do with the local rows. Pure — mirrored in
 * Swift by `signInOwnership`.
 *
 * `adopt` covers both "no notes are parked" (ordinary local mode, whose
 * ownerless rows merge into the account as they always have) and "the same
 * account came back". `wipe` is the only cross-account answer, and it runs
 * BEFORE any sync starts so the old account's rows can never be pushed into
 * the new one.
 */
export function resolveSignInOwnership(
  marker: PreviousOwner | null,
  signedInUserId: string,
): 'adopt' | 'wipe' {
  if (!marker) return 'adopt'
  return marker.userId === signedInUserId ? 'adopt' : 'wipe'
}

/**
 * Whether the sign-in gate must warn before the attempt: parked notes exist
 * and we cannot yet prove the user is going back to the account that owns
 * them. A typed email that matches the marker is that proof (the password
 * path); OAuth carries no such signal before the exchange, so it warns.
 */
export function shouldWarnBeforeSignIn(
  marker: PreviousOwner | null,
  noteCount: number,
  typedEmail?: string | null,
): boolean {
  if (!marker || noteCount === 0) return false
  if (!typedEmail || !marker.email) return true
  return typedEmail.trim().toLowerCase() !== marker.email.trim().toLowerCase()
}
